import { CANDIDATE_INPUT_INFO_TYPES, GENDERS, LOCAL_STORAGE_USER_LOGIN_INFO } from "@app/constants/Common";
import * as CandidateService from '@app/services/CandidateService';
import { checkExistsCommonFieldValues, resolveGenderCardPersonal } from "@app/utils/CandidateUtils";
import {
    stringToLowerCase
} from '@app/utils/CommonUtils';
import { showSuccessMessageSaveData } from "@pages/candidate/Common";
import AllUserInputField from "@pages/candidate/commonview/AllUserInputField";
import React, { useEffect } from 'react';
import { toast } from "react-toastify";


// 3 trường dùng chung của card này — chỉ gửi lên server khi user thực sự thay đổi
const SHARED_FIELDS = ['gender', 'caller_name_to_email', 'vocative'];

const GenderCard = ({
    candidateId,
    isEditGender=false,
    // updateCandidatePersonal,
    initPersonal,
    onChange,
    allInputInfo,
    mode = 'Edit' // Default to Edit mode for backward compatibility
}) => {
    const [personal, setPersonal] = React.useState({});
    const [isEdit, setIsEdit] = React.useState(isEditGender);
    const [userLoginInfo, setUserLoginInfo] = React.useState({});
    // Giá trị dùng chung vừa resolve — chỉ dùng để biết đã fetch xong hay chưa
    // (guard trước khi filter payload), KHÔNG dùng để so sánh dirty theo giá trị nữa.
    const [sharedBaseline, setSharedBaseline] = React.useState(null);
    // TASK-TEST-G-I-EMAIL-QUA-1786372420029 (bugfix): field nào user đã THỰC SỰ
    // sửa trong lần Edit hiện tại — dùng thay cho so sánh giá trị hiện tại vs
    // baseline, vì baseline có thể trùng giá trị mới (vd cùng là rỗng) dù user
    // chủ đích chọn lại giá trị đó → so sánh giá trị sẽ (sai) coi là "chưa sửa"
    // và loại field khỏi payload, khiến BE không ghi được lựa chọn của user.
    const [touchedFields, setTouchedFields] = React.useState(new Set());
    // TASK-TEST-G-I-EMAIL-QUA-1786372420029: ai đã nhập giá trị gender đang hiển thị
    // (null khi là giá trị của chính mình hoặc chưa có ai nhập) — tách riêng khỏi
    // `personal` để không bị gửi lẫn vào payload Save.
    const [genderInputBy, setGenderInputBy] = React.useState(null);

    useEffect(() => {
        if (initPersonal) {
            setPersonal(initPersonal);
        }
    }, [initPersonal]);
    useEffect(() => {
        setIsEdit(isEdit);
    }, [isEditGender]);

    const fetchPersonal = async (canId) => {
        const loginInfo = localStorage.getItem(LOCAL_STORAGE_USER_LOGIN_INFO)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_LOGIN_INFO))
            : {}
        const [ownRes, allRows] = await Promise.all([
            CandidateService.getCandidatePersonalDetail(canId).catch(() => ({})),
            CandidateService.searchAllInputInfo(canId, CANDIDATE_INPUT_INFO_TYPES.Personal).catch(() => []),
        ])
        // res.content = undefined khi sts_code = NOT_FOUND_DATA (user đang login chưa nhập gì cho UV này)
        const ownPersonal = (ownRes && ownRes.content) ? ownRes.content : {}
        // 3 trường của card là thông tin dùng chung → resolve từ bản ghi của mọi user
        const { genderInputBy: resolvedGenderInputBy, ...sharedFields } = resolveGenderCardPersonal({
            allPersonalRows: allRows || [],
            loginUserId: loginInfo.id,
            loginUserName: loginInfo.name,
        })
        setPersonal({...ownPersonal, ...sharedFields})
        setSharedBaseline(sharedFields)
        setGenderInputBy(resolvedGenderInputBy)
        setTouchedFields(new Set())
    };

    useEffect(() => {
        if (candidateId && mode !== 'Search') {
            fetchPersonal(candidateId)
        }
    }
    , [candidateId, mode]);

    useEffect(() => {
        if (!userLoginInfo.id && localStorage.getItem(LOCAL_STORAGE_USER_LOGIN_INFO)) {
            setUserLoginInfo(JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_LOGIN_INFO)))
        }
    }, [])

    const onChangePersonalInfo = (event) => {
        const touched = new Set(touchedFields)
        touched.add(event.target.name)
        if (mode !== 'Search' && event.target.name === 'gender') {
            if (event.target.value === 'nam') {
                personal.caller_name_to_email = 'anh';
            } else if (event.target.value === 'nữ') {
                personal.caller_name_to_email = 'chị';
            } else {
                personal.caller_name_to_email = '';
            }
            // Đổi gender tự động cập nhật caller_name_to_email (side-effect ở trên) →
            // phải đánh dấu field này là "đã sửa" để không bị filter khỏi payload khi Save.
            touched.add('caller_name_to_email')
        }
        personal[event.target.name] = event.target.value
        setPersonal({...personal})
        setTouchedFields(touched)

        if (onChange) {
            onChange(personal);
        }
    }

    const updateCandidatePersonal = (fields, type) => {
        // Check xem trong số các trường sử dụng chung, giá trị đã đc nhập bởi user khác cho UV này chưa
        // Nếu có user khác nhập cho UV này giá trị rồi => báo lỗi, ko save thông tin vào DB.
        // personal.allPersonalInfo = allPersonalInfo
        if (checkExistsCommonFieldValues(personal, userLoginInfo.id)) {
            return;
        }
        // setIsLoading(true)
        let payload = {...personal}
        if (sharedBaseline) {
            // BUGFIX TASK-TEST-G-I-EMAIL-QUA-1786372420029: dùng touchedFields (field
            // user THỰC SỰ tương tác trong lần Edit này) thay vì so sánh giá trị hiện
            // tại với baseline — so sánh giá trị sai khi giá trị mới TRÙNG baseline
            // (vd cả hai đều rỗng), khiến field bị loại khỏi payload dù user chủ đích
            // chọn lại giá trị đó (ví dụ xoá trắng "Gọi UV là" khi baseline cũng rỗng).
            const changedFields = SHARED_FIELDS.filter(field => touchedFields.has(field))
            if (changedFields.length === 0) {
                // Không sửa gì → không ghi giá trị dùng chung của user khác thành input của mình
                setIsEdit(false)
                return;
            }
            SHARED_FIELDS.forEach(field => {
                if (!changedFields.includes(field)) {
                    delete payload[field]
                }
            })
        }
        // TODO: Will set user login name
        // if (!payload['vocative']) {
        //     payload['vocative'] = userLoginInfo.name
        // }
        // fields.forEach(field => {
        //     payload[field] = personal[field]
        //     // TODO: Will set user login name
        //     if (field === 'vocative' && !payload[field]) {
        //         payload[field] = DEFAULT_ADMIN_NAME
        //     }
        // })
        CandidateService.updateCandidatePersonalDetail({candidate_id: candidateId, personal: payload}).then((res) => {
            console.log(JSON.stringify(res))
            showSuccessMessageSaveData(res.sts_code)
            setIsEdit(false)
            setTouchedFields(new Set())
        }).catch((error) => {
            toast.error(`Có lỗi xảy ra trong quá trình xử lý.`);
        }).finally(() => {
            // setIsLoading(false)
        });
    };

    return (
        <div className='card card-default'>
            <div className='card-header'>
                <h3 className='card-title text-bold'>Giới tính</h3>
                {mode === 'Edit' && (
                    <div className='card-tools'>
                        {isEdit &&
                            <button type='button'
                                    className='btn btn-default mr-2'
                                    onClick={() => {
                                        setIsEdit(false);
                                        setTouchedFields(new Set());
                                    }}
                            >
                                Cancel
                            </button>
                        }
                        {isEdit &&
                            <button type='button'
                                    className='btn btn-primary'
                                    onClick={() => {
                                        updateCandidatePersonal(['gender', 'caller_name_to_email', 'vocative'], 'update_gender');
                                    }}
                            >
                                Save
                            </button>
                        }
                        {!isEdit &&
                            <button type='button'
                                    className='btn btn-default'
                                    onClick={() => {
                                        setIsEdit(true);
                                    }}
                            >
                                Edit
                            </button>
                        }
                    </div>
                )}
            </div>
            <div className='card-body'>
                <div className='input-group'>
                    <label className={'w-100'}>Giới tính</label>
                    <select className='form-control'
                            name={'gender'}
                            disabled={!isEdit}
                            value={personal.gender ? personal.gender : ''}
                            onChange={event => onChangePersonalInfo(event)}>
                        <option value=''>&nbsp;</option>
                        {GENDERS.map(item => {
                            return <option value={item.toLowerCase()}
                                           key={`gender_${item}`}>{item}</option>
                        })}
                    </select>
                    <AllUserInputField arrInfo={allInputInfo.allPersonalInfo}
                                       field='gender'/>
                    {genderInputBy &&
                        <small className='text-muted w-100 mt-1'>
                            Nhập bởi: {genderInputBy.name || `User #${genderInputBy.id}`}
                        </small>
                    }
                </div>
                {mode !== 'Search' && (
                    <>
                   
                <div className='input-group mt-3'>
                    <label className={'w-100'}>Gọi UV là (email)</label>
                    <select className='form-control'
                            name={'caller_name_to_email'}
                            disabled={!isEdit}
                            value={personal.caller_name_to_email ? personal.caller_name_to_email : ''}
                            onChange={event => onChangePersonalInfo(event)}>
                        <option value=''>&nbsp;</option>
                        {personal.gender !== 'nữ' &&
                            <option value='anh'>anh</option>
                        }
                        {personal.gender !== 'nam' &&
                            <option value='chị'>chị</option>
                        }
                    </select>
                </div>
                <div className='input-group mt-3'>
                    <label className={'w-100'}>User xưng là (email)</label>
                    <select className='form-control'
                            name={'vocative'}
                            disabled={!isEdit}
                            value={personal.vocative ? personal.vocative : ""}
                            onChange={event => onChangePersonalInfo(event)}>

                        {personal.caller_name_to_email === 'anh' || personal.caller_name_to_email === 'chị' ?
                            <option value={stringToLowerCase(userLoginInfo.name)}>
                                {userLoginInfo.name}
                            </option>
                            :
                            <>
                                <option value=''>&nbsp;</option>
                                <option
                                    value={stringToLowerCase(userLoginInfo.name)}>{userLoginInfo.name}</option>
                                <option
                                    value={userLoginInfo.vocative}>{userLoginInfo.vocative}</option>
                            </>
                        }
                    </select>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default GenderCard;


